import { useContext, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { CType } from 'models/Contentful/types';
import { IQuickstartStep } from 'models/Quickstart/@types';
import ImageComponent from 'components/Image';
import { markStepAsCompleted } from 'models/Quickstart/api';
import AuthContext from 'AppContext/AuthProvider';
import { TeacherOnboardingTaskCompletion } from 'models/auth/@types';
import Link, { LinkComponentProps } from 'components/Link';
import styles from './quickstart.module.sass';
import headerStyles from '../Header/header.module.sass';
import QuickstartContext from '../../AppContext/Quickstart/context';
import Lightbulb from './icons/lightbulb';
import Close from './icons/close';
import Check from './icons/check';
import RichText from '../RichText';
import Button from '../Button';

const Quickstart = () => {
  const { isOpen, quickStart, closeQuickstart } = useContext(QuickstartContext);
  const { appUser } = useContext(AuthContext);
  const [expandedStep, setExpandedStep] = useState<Record<string, boolean>>({});
  const quickstartRef = useRef<HTMLDivElement>(null);
  const [checkedSteps, setCheckedSteps] = useState<Record<string, boolean>>({});
  const [isQuickstartCompleted, setIsQuickstartCompleted] = useState<boolean>(false);

  useEffect(() => {
    const completedSteps = appUser?.teacher_onboarding_task_completions?.reduce(
      (acc: Record<string, boolean>, task: TeacherOnboardingTaskCompletion) => {
        acc[task.task_id as string] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    );
    setCheckedSteps(completedSteps || {});
  }, [appUser?.teacher_onboarding_task_completions]);

  useEffect(() => {
    const uniqueSteps = Array.from(
      new Set(quickStart?.fields.steps?.map(step => step.sys.id).filter(Boolean) || []),
    );
    const areAllStepsCompleted =
      Object.values(checkedSteps).filter(Boolean).length === uniqueSteps.length;
    setIsQuickstartCompleted(areAllStepsCompleted);
  }, [checkedSteps, quickStart]);

  useEffect(() => {
    const correctTop = () => {
      const header = document.querySelector(`.${headerStyles.stickyHeader}`);
      if (header && quickstartRef.current) {
        const headerHeight = header.getBoundingClientRect().height;
        quickstartRef.current.style.top = `${headerHeight}px`;
      }
    };
    const headerObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (
          mutation.type === 'childList' &&
          mutation.target instanceof HTMLElement &&
          mutation.target.classList.contains(headerStyles.stickyHeader)
        ) {
          correctTop();
        }
      });
    });
    headerObserver.observe(document.querySelector('#__next') as Node, {
      childList: true,
      subtree: true,
    });
    window.addEventListener('resize', correctTop);
    correctTop();
    return () => {
      headerObserver.disconnect();
      window.removeEventListener('resize', correctTop);
    };
  }, []);

  const handleCheckStep = (id: string) => {
    setCheckedSteps(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
    const areAllStepsCompleted =
      Object.values(checkedSteps).filter(Boolean).length === quickStart?.fields.steps.length;
    setIsQuickstartCompleted(areAllStepsCompleted);
    markStepAsCompleted(id);
  };

  return (
    <div className={clsx(styles.quickstart, isOpen && styles.open)} ref={quickstartRef}>
      <div className={styles.quickstartContainer}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <h2>
              <span className={styles.icon}>
                <Lightbulb />
              </span>
              <span className={styles.title}>Quickstart</span>
            </h2>
            <button
              type="button"
              onClick={closeQuickstart}
              className={styles.closeButton}
              aria-label="Close Quickstart"
            >
              <Close />
            </button>
          </div>
          {isQuickstartCompleted && (
            <div className={styles.congratulations}>Congratulations! Quickstart completed!</div>
          )}
        </div>
        <div className={styles.content}>
          {quickStart?.fields.steps.map((step: CType<IQuickstartStep>) => (
            <Accordion
              key={step.sys.id}
              classes={styles.accordion}
              disableGutters
              square
              expanded={expandedStep[step.fields.title]}
              onChange={() =>
                setExpandedStep(prev => ({
                  ...prev,
                  [step.fields.title]: !prev[step.fields.title],
                }))
              }
            >
              <AccordionSummary>
                <button
                  type="button"
                  className={clsx(
                    styles.stepCheckButton,
                    checkedSteps[step.sys.id as string] && styles.checked,
                  )}
                  onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleCheckStep(step.sys.id as string);
                  }}
                >
                  <Check />
                </button>
                <span className={styles.stepTitle}>{step.fields.title}</span>
                <span className={styles.stepArrowContainer}>
                  <span
                    className={clsx(
                      styles.stepArrow,
                      expandedStep[step.fields.title] && styles.open,
                    )}
                  />
                </span>
              </AccordionSummary>
              <AccordionDetails>
                {step.fields.media && <ImageComponent image={step.fields.media} />}
                {step.fields.description && (
                  <RichText document={step.fields.description} classes={styles.richText} />
                )}
                {step.fields.link && (
                  <Link
                    {...(step.fields.link as unknown as LinkComponentProps)}
                    classes={clsx('is-secondary-default button', styles.quickstartButton)}
                  />
                )}
                {/* button to mark as completed */}
                {!checkedSteps[step.sys.id as string] && (
                  <Button
                    classes={clsx('is-tertiary-main button', styles.markAsCompletedButton)}
                    onClick={() => handleCheckStep(step.sys.id as string)}
                  >
                    Mark as Completed
                  </Button>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Quickstart;
